import React, { PropsWithChildren, ReactElement } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './SetupStepsSection.css';
import transformHTMLParserLinks, { Options } from 'react-html-parser';
import { useWidthContext } from '../../providers/Width';
import { ButtonWrapper, LinkWrapper } from '../LinkWrapper/LinkWrapper';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { retrieveDBOsgiUrl, safeParse, wrapTrademarkSuperscript } from 'utils/cob-utils';
import { Button, Row, Col, Section } from '../../importCL';

interface ITitleAndDescProps {
    title: string;
    description: string;
    iconPath: string;
    iconAltText: string;
}
interface ISetupStepsSectionProps extends MappedComponentProperties {
    highYieldVariation?: boolean;
    setUpItems?: ITitleAndDescProps[];
    bottomTextDesktop?: string;
    bottomTextMobile?: string;
    imgPath?: string;
    imgAltText?: string;
    ctaText?: string;
    ctaLink?: string;
    ctaId?: string;
    sideTitle?: string;
    chevronLinkVariant?: string;
    sideLinkText?: string;
    sideLink?: string;
    sideDesc?: string;
    sideSubtitle?: string;
}

export const SetupStepsSectionEditConfig = {
    emptyLabel: 'SetupStepsSection',
    isEmpty: (props: PropsWithChildren<ISetupStepsSectionProps>) => {
        return !props || !props.setUpItems;
    },
};

const parseContent = (htmlInput: string, options?: Options): ReactElement[] => {
    if (!htmlInput) return;
    const renderedResult = transformHTMLParserLinks(wrapTrademarkSuperscript(htmlInput), options);
    return renderedResult.map((el: ReactElement, index: number) => {
        return el.type === 'a' ? <LinkWrapper key={index} {...el.props} /> : el;
    });
};

const getSteps = (highYield:boolean, items: ITitleAndDescProps[] | undefined) => {
    if (!items) {
        return;
    }
    return items.slice(0, 3).map((item, index) =>
        <div key={`textStep${index}`} className={`SetupStepsSection__stepsContainer--${highYield ? 'highYield': 'mobilePay'}`}>
            <div className={`SetupStepsSection__steps--${highYield ? 'highYield': 'mobilePay'}`}>
                <div className={`SetupStepsSection__iconContainer--${highYield ? 'highYield' : 'mobilePay'}`}>
                    <img className={`SetupStepsSection__icons--${highYield ? 'highYield' : 'mobilePay'}`}
                        loading='lazy'
                        src={item.iconPath} alt={item.iconAltText}/>
                </div>
                <div className={`SetupStepsSection__stepText--${highYield ? 'highYield': 'mobilePay'}`}>
                    <h3 className={`SetupStepsSection__title--${highYield ? 'HighYield': 'mobilePay'}`}>{parseContent(item.title)}</h3>
                    <div className={`SetupStepsSection__desc--${highYield ? 'HighYield': 'mobilePay'}`}>{parseContent(item.description)}</div>
                </div>
            </div>
        </div>);
};


const SetupStepsSection = (props: PropsWithChildren<ISetupStepsSectionProps>): JSX.Element => {
    const { highYieldVariation = false } = props;
    const { width } = useWidthContext();
    const bottomText = width > 768 ? props.bottomTextDesktop : props.bottomTextMobile;

    const digitalBankMetaProperty = safeParse(PathUtils.getMetaPropertyValue('digitalBankTerms'));
    const ctaUrl = props.highYieldVariation && digitalBankMetaProperty ? retrieveDBOsgiUrl(digitalBankMetaProperty?.baseUrl) : props?.ctaLink;


    return (
        <div id='setup-steps-section' className='SetupStepsSection'>
            {props.highYieldVariation &&
                (<Section bgColor='light-grey'>
                    <Row className='SetupStepsSection__bodySection--highYield'>
                        <Col sm={{ span: 6 }} md={5}>
                            {getSteps(highYieldVariation, props.setUpItems)}
                            <div className='SetupStepsSection__bottomBtn'>
                                {ctaUrl && props.ctaText && 
                                    <ButtonWrapper size='medium' href={ctaUrl} id={props.ctaId}>{parseContent(props.ctaText)}</ButtonWrapper>
                                }
                            </div>
                        </Col>
                        <Col sm={{ span: 6 }} md={{ span: 5, offset: 2 }} className='SetupStepsSection__text--highYield'>
                            <div className='SetupStepsSection__sideTitle'>{parseContent(props.sideTitle)}</div>
                            {props.chevronLinkVariant && 
                                <a className='SetupStepsSection__sideLink' href={props.sideLink}>
                                    <Button className='SetupStepsSection__sideLinkText' icon='chevron'  size='medium'  target='_self' variant='link' >
                                        {transformHTMLParserLinks(wrapTrademarkSuperscript(props.sideLinkText)) as unknown as ReactElement}
                                    </Button>
                                </a>
                            }
                            {!props.chevronLinkVariant && props.sideLinkText && 
                                <a className='SetupStepsSection__sideLink' href={props.sideLink}>
                                    {parseContent(props.sideLinkText)}
                                </a>
                            }
                            <div className='SetupStepsSection__sideDescription'>{props.sideDesc && parseContent(props.sideDesc)}</div>
                            <h2 className='SetupStepsSection__sideSubTitle'>{parseContent(props.sideSubtitle)}</h2>
                            <div className='SetupStepsSection__bottomText'>
                                {bottomText && (parseContent(bottomText))}
                            </div>
                        </Col>
                    </Row>
                </Section>)
            }
            {!props.highYieldVariation && 
                <Section containerClass='SetupStepsSection__mobilePay--customPadding' className='SetupStepsSection__mobilePay' bgColor='light-grey'>
                    <Row align='end' justify='end' className='SetupStepsSection__bodySection--mobilePay'>
                        <Col  sm={6} md={{ span: 7, order: 1 }} className='px-2 [@media(min-width:415px)]:px-12 [@media(min-width:1201px)]:px-0'>
                            <div>{getSteps(highYieldVariation, props.setUpItems)}</div>
                            <div className='SetupStepsSection__bottomSection--mobilePay'>
                                <div>
                                    <div className='SetupStepsSection__bottomText--mobilePay'>{bottomText && parseContent(bottomText)}</div>
                                </div>
                            </div>
                        </Col>
                        <Col md={{ order: 2, offset: 1, span: 4 }} className='SetupStepsSection__imgCol'>
                            <div className='SetupStepsSection__imgContainer'>
                                <img className='SetupStepsSection__img' loading='lazy' src={props.imgPath} alt={props.imgAltText}/>
                            </div>
                        </Col>
                    </Row>
                </Section>
            }
        </div>
    );
};
export default SetupStepsSection;
